import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ChangeDetectorRef,
  Injector,
  HostBinding
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

export type CountyPick = { county: string; state: string; placeId?: string };

@Component({
    selector: 'county-autocomplete-input',
    templateUrl: './county-autocomplete-input.component.html',
    styleUrls: ['./county-autocomplete-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountyAutocompleteInputComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CountyAutocompleteInputComponent implements ControlValueAccessor {
  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) {}

  get ngControl(): NgControl | null {
    return this.injector.get(NgControl, null);
  }

  @ViewChild('countyInput', { static: true }) countyInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown', { static: false }) dropdownRef!: ElementRef<HTMLUListElement>;

  @Output() selected = new EventEmitter<CountyPick>();

  @Input('id') inputId?: string;
  @HostBinding('attr.id') id: string = '';
  @Input('name') inputName?: string;
  @HostBinding('attr.name') hostName: string = '';

  @Input() inputClass: string | string[] = 'form-control';
  @Input() placeholder: string = 'County';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() autocompleteDisabled?: boolean;

  @Input() minChars = 2;
  @Input() debounceMs = 350;

  @Input() includedPrimaryTypes: string[] = ['administrative_area_level_2'];
  @Input() regionCodes: string[] = ['US'];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  writeValue(value: string): void { this.inputValue = value || ''; }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  inputValue = '';
  loading = false;
  errorMessage: string | null = null;
  showDropdown = false;
  debounceTimeout: any;

  options: Array<{
    placeId: string;
    county: string;
    stateAbbr: string;
    display: string;
  }> = [];

  private stateCache = new Map<string, string>();

  ngOnInit(): void {
    this.id = this.inputId || `county-${Math.random().toString(36).slice(2)}`;
    this.hostName = this.inputName || `county-${Math.random().toString(36).slice(2)}`;
  }

  ngAfterViewInit() {
    if (!this.isPlacesV3Loaded()) {
      this.errorMessage = 'Google Maps Places API v3 is not loaded.';
      this.cdr.detectChanges();
      return;
    }
    this.cdr.detectChanges();
  }

  onInput(e: Event) {
    const value = (e.target as HTMLInputElement).value ?? '';
    this.inputValue = value;
    this.errorMessage = null;

    this.onChange(this.inputValue);

    if (this.autocompleteDisabled) return;

    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
    if (!value.trim() || value.trim().length < this.minChars) {
      this.options = [];
      this.showDropdown = false;
      return;
    }

    this.loading = true;
    this.debounceTimeout = setTimeout(() => {
      this.fetchPredictions(value.trim());
    }, this.debounceMs);
  }

  onFocus(): void {
    if (this.options.length > 0) this.showDropdown = true;
  }

  onBlur(): void {
    this.onTouched();
    setTimeout(() => (this.showDropdown = false), 200);
  }

  onClear(): void {
    this.inputValue = '';
    this.options = [];
    this.showDropdown = false;
    this.errorMessage = null;
    this.onChange(this.inputValue);
  }

  private isPlacesV3Loaded(): boolean {
    return !!(window as any).google?.maps?.places
      && typeof (window as any).google.maps.places.AutocompleteSuggestion?.fetchAutocompleteSuggestions === 'function'
      && typeof (window as any).google.maps.places.Place === 'function';
  }

  private async fetchPredictions(input: string) {
    if (!this.isPlacesV3Loaded()) {
      this.loading = false;
      this.errorMessage = 'Google Maps Places API v3 is not loaded.';
      this.options = [];
      this.showDropdown = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      const placesNS: any = (window as any).google.maps.places;

      const result = await placesNS.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: this.includedPrimaryTypes,
        includedRegionCodes: (this.regionCodes || []).map(x => (x || '').toUpperCase()).filter(Boolean)
      });

      if (!result?.suggestions?.length) {
        this.loading = false;
        this.options = [];
        this.showDropdown = false;
        this.errorMessage = null;
        this.cdr.detectChanges();
        return;
      }

      const top = result.suggestions.slice(0, 6);
      const enriched = await Promise.all(top.map(async (s: any) => {
        const placeId: string = s.placePrediction?.placeId;
        const rawMain: string = s.placePrediction?.text?.text || '';
        const county = stripCounty(firstSegment(rawMain));

        let abbr = this.stateCache.get(placeId) || '';
        if (!abbr) {
          const place = new placesNS.Place({ id: placeId });
          await place.fetchFields({ fields: ['addressComponents'] });
          const comps: any[] = place.addressComponents ?? [];
          const stateComp = comps.find(c => c.types?.includes('administrative_area_level_1'));
          abbr = (stateComp?.shortText || stateComp?.longText || '').toUpperCase();
          if (abbr) this.stateCache.set(placeId, abbr);
        }

        const display = abbr ? `${county}, ${abbr}` : county;
        return { placeId, county, stateAbbr: abbr, display };
      }));

      this.loading = false;
      this.options = enriched;
      this.showDropdown = this.options.length > 0;
      this.cdr.detectChanges();

    } catch (e) {
      this.loading = false;
      this.options = [];
      this.showDropdown = false;
      this.errorMessage = 'Failed to fetch county suggestions.';
      this.cdr.detectChanges();
    }

    function firstSegment(txt: string): string {
      return (txt || '').split(',')[0]?.trim() || '';
    }
    function stripCounty(t: string): string {
      const s = (t || '').trim();
      return s.replace(/\s+County$/i, '').trim();
    }
  }

  async onSelectOption(opt: { placeId: string; county: string; stateAbbr: string }) {
    if (!this.isPlacesV3Loaded()) {
      this.errorMessage = 'Google Maps Places API v3 is not loaded.';
      return;
    }

    this.loading = true;
    this.showDropdown = false;

    try {
      const placesNS: any = (window as any).google.maps.places;
      const place = new placesNS.Place({ id: opt.placeId });

      await place.fetchFields({ fields: ['addressComponents'] });

      const comps: any[] = place.addressComponents ?? [];
      const countyComp = comps.find(c => c.types?.includes('administrative_area_level_2'));
      const stateComp  = comps.find(c => c.types?.includes('administrative_area_level_1'));

      const county = stripCounty(opt.county || countyComp?.longText || countyComp?.shortText || '');
      const stateUpper = (stateComp?.shortText || stateComp?.longText || opt.stateAbbr || '').toUpperCase();

      this.inputValue = county;
      this.onChange(this.inputValue);

      this.selected.emit({
        county,
        state: stateUpper,
        placeId: opt.placeId
      });

      this.options = [];
      this.loading = false;
      this.cdr.detectChanges();

    } catch (err) {
      console.error('Place.fetchFields error:', err);
      this.loading = false;
      this.errorMessage = 'Failed to retrieve county details.';
      this.cdr.detectChanges();
    }

    function stripCounty(t: string): string {
      const s = (t || '').trim();
      return s.replace(/\s+County$/i, '').trim();
    }
  }
}
